import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { FuellingProvider } from 'app/core/models/fuellingProvider';
import { Supplier } from 'app/core/models/supplier';
import { FuellingproviderService } from 'app/core/services/fuellingprovider.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, of, ReplaySubject, Subscription } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { tableEventChange } from '../table-utils/classes/tableChangeEvent';
import { actionType } from '../table-utils/enums/actionType';
import { columnType } from '../table-utils/enums/columnType';

@Component({
  selector: 'app-fuellingProvider-page',
  templateUrl: './fuellingProvider-page.component.html'
})
export class FuellingProviderPageComponent extends AbstractMerchantSupplierComponent implements OnInit {

  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;
  isDeletedIncluded : boolean = false;

  statusItems: Array<String> = new Array<String>();

  columns: { 
    data : { 
      title: string; 
      type: columnType 
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0, 
    sort: "createdDate,DESC", 
    size: 20
  };

  criteria : FuellingProvider = new FuellingProvider();

  columnPropertyMapping = {
    "FUELLINGPROVIDER_ID" : "id",
    "SUPPLIER_NAME" : "supplierId",
    "FUELLINGPROVIDER_TYPE" : "fuellingProviderType",
    "ACTIONS" : "actions"
  };

  searchForm : FormGroup = new FormGroup({
    id: new FormControl(),
    supplierId: new FormControl()
  });

  modalResponseSubscription : Subscription;

  isSearchByFPId : boolean = true;
  suppliers : Array<Supplier>;
  
  constructor(
    protected log: NGXLogger,
    private fuellingproviderService: FuellingproviderService,
    private notifService: NotifMessageService,
    public router: Router,
    private roleGuard: AuthenticatedRouteGuard,
    private keycloakService: KeycloakService,
    protected supplierService: SupplierService,
    protected posService: PosService,
    protected merchantService: MerchantService
  ) { super(supplierService, merchantService, posService, log, router); }


  ngOnInit() {
    this.columns = {
      data : [
      { title: "FUELLINGPROVIDER_ID", type: columnType.text },
      { title: "SUPPLIER_NAME", type: columnType.text },
      { title: "FUELLINGPROVIDER_TYPE", type: columnType.translateText },
      { title: "ACTIONS", type: columnType.actions }
    ],
    clickable : true
    };
     this.supplierService.getAllSuppliers().subscribe(suppliers => {
      this.suppliers = suppliers.content;
    });
    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('FUELING_PROVIDER_ACCOUNT_READ')) {
          this.loadData();
        } 
      }
    });
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  formatFuellingProvider(fuellingProvider : FuellingProvider, supplierName : string) {
    const actions = [];

    if (this.roleGuard.isEnabled('FUELING_PROVIDER_ACCOUNT_EDIT') && !fuellingProvider.deleted) {
      actions.push({
        "name" : "EDIT",
        "type" : actionType.navigation,
        "value": `/service/fuellingprovider/edit/${fuellingProvider.id}`
      });
    }
    if (this.roleGuard.isEnabled('FUELING_PROVIDER_ACCOUNT_READ')) {
      actions.push({
        "name" : "DETAILS",
        "type" : actionType.navigation,
        "value": `/service/fuellingprovider/${fuellingProvider.id}`
      });
    }
    if (this.roleGuard.isEnabled('FUELING_PROVIDER_ACCOUNT_DELETE') && !fuellingProvider.deleted) {
      actions.push({
        "name" : "DELETE",
        "type" : actionType.eventEmitter,
        "value": fuellingProvider.id
      })
    }

    return {
      id: fuellingProvider.id,
      FUELLINGPROVIDER_ID: fuellingProvider.id,
      SUPPLIER_NAME: `${supplierName} (${fuellingProvider.supplierId})`,
      FUELLINGPROVIDER_TYPE: fuellingProvider.fuellingProviderType,
      deleted: fuellingProvider.deleted,
      ACTIONS: actions
    }
  }

  loadData(isLoadingSearch : boolean = false) : void {
    this.isLoadingResults = true;
    this.fuellingproviderService
    .findByCriteria(this.criteria, this.pager, this.isDeletedIncluded)
    .pipe(
      tap(fuellingProviders => this.totalLength$.next(fuellingProviders.totalElements)),
      switchMap(fuellingProviders => {
        if (fuellingProviders.content.length !== 0) {
          const fuellingProviders$ = fuellingProviders.content.map(fuellingProvider => {
            return this.getSupplierName(fuellingProvider.supplierId).pipe(
              map(supplierName => {
                  return this.formatFuellingProvider(fuellingProvider, supplierName)
              }
              ));
          });
          return combineLatest(fuellingProviders$)
        }
        if (isLoadingSearch) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
        return of([]);
      }),
      map(fuellingProviders => {
        this.isLoadingResults = false;
        if (fuellingProviders === null) {
          return [];
        }
        return fuellingProviders;
      }),
    ).subscribe(fuellingProviders =>  {
      this.dataSubject$.next(fuellingProviders);
    })
  }

  deleteButton(id) {
    this.notifService.emitModalWithDetails("DELETE_FUELLINGPROVIDER",["FUELLINGPROVIDER_DELETION","DELETE_FUELLINGPROVIDER"]);
    this.modalResponseSubscription = this.notifService.modalResponseSubject$.subscribe(resp => {
      console.log("Get resp", resp);
      if (resp) {
        this.fuellingproviderService.deleteFuellingProviderAccount(id).subscribe(
          () => {
            this.loadData();
            this.notifService.notifySuccess('FUELLINGPROVIDER_DELETED');
            this.modalResponseSubscription.unsubscribe();
          }
        );
      } else {
        this.modalResponseSubscription.unsubscribe();
      }
    });
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex, 
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "lastModifiedDate,DESC", 
      size: event.pageSize
    }
    this.loadData();
  }

  search() : void {
    this.criteria = new FuellingProvider();
    for (let field in this.searchForm.value) {
        if (!this.searchForm.value[field]) {
          delete this.searchForm.value[field];
        } else {
            this.criteria[field] = this.searchForm.value[field]
          }
    }
    this.loadData(true);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new FuellingProvider();
    this.loadData();
  }

  includeDeleted(event : MatCheckboxChange) : void {
    this.isDeletedIncluded = event.checked;
    this.loadData();
  }

  details(id : number) : void{
    this.router.navigate([`/service/fuellingprovider/${id}`]);
  }

}
